import { BASE_URL } from './apiClient';

// Auth endpoints — only /me remains; identity is owned by Zitadel.
export const USER_URLS = {
  getMe: `${BASE_URL}/api/me`,
};

// Custom-login (backend runs the Zitadel Session-API flow) + self-registration.
export const AUTH_URLS = {
  login: `${BASE_URL}/api/auth/login`,
  requestAccess: `${BASE_URL}/api/auth/request-access`,
  verifyEmail: `${BASE_URL}/api/auth/verify-email`,
  googleStart: `${BASE_URL}/api/auth/google/start`,
  googleCallback: `${BASE_URL}/api/auth/google/callback`,
};

// Catalog orders endpoints
export const CATALOG_URLS = {
  createOrder: `${BASE_URL}/api/catalog-orders/catalog-orders/`,
  getAllOrders: `${BASE_URL}/api/catalog-orders/catalog-orders/`,
  updateOrder: (orderId) => `${BASE_URL}/api/catalog-orders/catalog-orders/${orderId}`,
  deleteOrder: (orderId) => `${BASE_URL}/api/catalog-orders/catalog-orders/${orderId}`,
  downloadZip: (orderId) => `${BASE_URL}/api/catalog-orders/catalog-orders/${orderId}/download-zip`,
  downloadProduct: (orderId) =>
    `${BASE_URL}/api/catalog-orders/catalog-orders/${orderId}/download-product`,
};

// Tasking orders endpoints
export const TASKING_URLS = {
  createOrder: `${BASE_URL}/api/catalog-orders/tasking-orders/`,
  getAllOrders: `${BASE_URL}/api/catalog-orders/tasking-orders/`,
  updateOrder: (orderId) => `${BASE_URL}/api/catalog-orders/tasking-orders/${orderId}`,
  deleteOrder: (orderId) => `${BASE_URL}/api/catalog-orders/tasking-orders/${orderId}`,
};

// Satellite endpoints
export const SATELLITE_URLS = {
  getTrack: `${BASE_URL}/api/satellites/track`,
};

// Preview and data processing endpoints
export const IMAGE_URLS = {
  getPreview: (imageId) => `${BASE_URL}/api/preview/${imageId}`,
  getBestScenes: `${BASE_URL}/api/best-scenes`, // TODO: no backend route yet
  downloadPreview: (imageId) => `${BASE_URL}/api/download-preview/${imageId}`,
  itemsByIds: `${BASE_URL}/api/imagery/items-by-ids`,
};

// Search and catalog endpoints
export const SEARCH_URLS = {
  catalog: `${BASE_URL}/api/search`,
  // Internal STAC "My Images" explore (role-gated; previews + tiles come back
  // as relative proxy paths that the thunk prefixes with BASE_URL).
  myImages: `${BASE_URL}/api/search/my-images`,
  myImagesAll: `${BASE_URL}/api/my-images/all`,
  myImagesUsage: `${BASE_URL}/api/my-images/usage`,
  myImagesQuote: `${BASE_URL}/api/my-images/quote`,
  myImagesDownload: `${BASE_URL}/api/my-images/download`,
  // Read-only ISRO Bhoonidhi explore (footprints + quicklook previews only).
  bhoonidhiExplore: `${BASE_URL}/api/search/bhoonidhi-explore`,
  // Stage selected Bhoonidhi scenes into the user's own cart (role-gated).
  // Returns { status: 'added', added, failed, portal_url } or
  // { status: 'otp_required', pending_token } when the session lapsed.
  bhoonidhiCartAdd: `${BASE_URL}/api/search/bhoonidhi-cart/add`,
  bhoonidhiCartVerifyOtp: `${BASE_URL}/api/search/bhoonidhi-cart/verify-otp`,
  // Silently establishes the Bhoonidhi session from seeded creds right after
  // Zitadel login → { status: 'connected' | 'otp_required' (+pending_token) |
  // 'not_configured' }; verify-otp completes an OTP challenge.
  bhoonidhiAccountConnect: `${BASE_URL}/api/bhoonidhi-account/connect`,
  bhoonidhiAccountVerifyOtp: `${BASE_URL}/api/bhoonidhi-account/verify-otp`,
  // In-app configurator (Bhoonidhi Settings): GET status / DELETE disconnect on
  // the base, POST credentials to save+validate, POST reverify to re-login with
  // the stored password.
  bhoonidhiAccount: `${BASE_URL}/api/bhoonidhi-account`,
  bhoonidhiAccountCredentials: `${BASE_URL}/api/bhoonidhi-account/credentials`,
  bhoonidhiAccountReverify: `${BASE_URL}/api/bhoonidhi-account/reverify`,
  checkFeasibility: `${BASE_URL}/api/check-feasibility`,
  downloadFeasibility: `${BASE_URL}/api/export-feasibility`,
  downloadFeasibilityXLSX: `${BASE_URL}/api/export-feasibility-xlsx`,
  downloadFeasibilityCSVCombined: `${BASE_URL}/api/export-feasibility-csv-combined`,
  getProducts: `${BASE_URL}/api/products`,
};

// Super-Admin credit management (Platform Options → Credit Management).
// All Super-Admin-gated on the backend (403 otherwise).
export const ADMIN_URLS = {
  credits: `${BASE_URL}/api/admin/credits`,
  creditSettings: `${BASE_URL}/api/admin/credits/settings`,
  userCredit: (sub) => `${BASE_URL}/api/admin/credits/users/${encodeURIComponent(sub)}`,
  analyticsUsers: `${BASE_URL}/api/admin/analytics/users`,
};

// AOI endpoints
export const AOI_URLS = {
  getSavedAOIs: `${BASE_URL}/api/saved-aois`,
  saveAOI: `${BASE_URL}/api/saved-aois`,
  deleteAOI: (aoiId) => `${BASE_URL}/api/saved-aois/${aoiId}`,
};

// IMINT (imagery intelligence) endpoints
export const IMINT_URLS = {
  getTargets: `${BASE_URL}/api/imint/targets`,
  createTarget: `${BASE_URL}/api/imint/targets`,
  deleteTarget: (targetId) => `${BASE_URL}/api/imint/targets/${targetId}`,
  getDetectors: `${BASE_URL}/api/imint/detectors`,
  uploadImage: `${BASE_URL}/api/imint/images`,
  presignImage: `${BASE_URL}/api/imint/images/presign`,
  imageFromS3: `${BASE_URL}/api/imint/images/from-s3`,
  getTasks: (targetId) => `${BASE_URL}/api/imint/tasks?target_id=${targetId}`,
  createTask: `${BASE_URL}/api/imint/tasks`,
  getTask: (taskId) => `${BASE_URL}/api/imint/tasks/${taskId}`,
  updateTask: (taskId) => `${BASE_URL}/api/imint/tasks/${taskId}`,
  deleteTask: (taskId) => `${BASE_URL}/api/imint/tasks/${taskId}`,
  getDetections: (taskId) => `${BASE_URL}/api/imint/detections?task_id=${taskId}`,
  createDetection: `${BASE_URL}/api/imint/detections`,
  detection: (id) => `${BASE_URL}/api/imint/detections/${id}`,
  getReports: (targetId) => `${BASE_URL}/api/imint/reports?target_id=${targetId}`,
  createReport: `${BASE_URL}/api/imint/reports`,
  getReport: (reportId) => `${BASE_URL}/api/imint/reports/${reportId}`,
};

// Order status log endpoints
export const ORDER_LOG_URLS = {
  getStatusLogs: (orderType, orderId) =>
    `${BASE_URL}/api/orders/${orderType}/${orderId}/status-logs`,
};

// Utility function to build URLs with query parameters
export const buildUrl = (baseUrl, params = {}) => {
  const query = Object.entries(params)
    .filter(([_, value]) => value !== undefined && value !== null)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');
  return query ? `${baseUrl}?${query}` : baseUrl;
};
